/**
 * Non-URL state of the overview / tape: the action under the pointer, the
 * pinned (clicked) action, the tx-story modal, legend toggles and pane
 * collapse (persisted).
 */
import { create } from "zustand";
import type { Marker } from "@/api/types";
import { loadLocal, saveLocal } from "@/lib/storage";
import { MARKER_FAMILIES, type MarkerFamily } from "./lib/markers";

/** What the side panel shows for a hovered / clicked marker or row. */
export interface ActionRef {
  tx: string;
  address: string;
  /** the marker, when it came from the price pane (instant detail before the row is fetched) */
  marker?: Marker;
}

interface OverviewState {
  resetJobState: () => void;
  hovered: ActionRef | null;
  pinned: ActionRef | null;
  txModal: string | null;
  setHovered: (a: ActionRef | null) => void;
  setPinned: (a: ActionRef | null) => void;
  setTxModal: (tx: string | null) => void;
  markerFamilies: MarkerFamily[];
  toggleMarkerFamily: (f: MarkerFamily) => void;
  tradersCollapsed: boolean;
  toggleTraders: () => void;
  holdersCollapsed: boolean;
  toggleHolders: () => void;
  netflowCollapsed: boolean;
  toggleNetflow: () => void;
}

export const useOverviewStore = create<OverviewState>((set, get) => ({
  resetJobState: () => set({ hovered: null, pinned: null, txModal: null }),
  hovered: null,
  pinned: null,
  txModal: null,
  setHovered: (a) => {
    const cur = get().hovered;
    if (cur === a || (cur && a && cur.tx === a.tx && cur.address === a.address)) return;
    set({ hovered: a });
  },
  setPinned: (a) => set({ pinned: a }),
  setTxModal: (tx) => set({ txModal: tx }),
  markerFamilies: loadLocal<MarkerFamily[]>("overview.markers", [...MARKER_FAMILIES]),
  toggleMarkerFamily: (f) => {
    const cur = get().markerFamilies;
    const next = cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f];
    saveLocal("overview.markers", next);
    set({ markerFamilies: next });
  },
  tradersCollapsed: loadLocal("overview.traders.collapsed", false),
  toggleTraders: () => {
    const v = !get().tradersCollapsed;
    saveLocal("overview.traders.collapsed", v);
    set({ tradersCollapsed: v });
  },
  holdersCollapsed: loadLocal("overview.holders.collapsed", false),
  toggleHolders: () => {
    const v = !get().holdersCollapsed;
    saveLocal("overview.holders.collapsed", v);
    set({ holdersCollapsed: v });
  },
  netflowCollapsed: loadLocal("overview.netflow.collapsed", false),
  toggleNetflow: () => {
    const v = !get().netflowCollapsed;
    saveLocal("overview.netflow.collapsed", v);
    set({ netflowCollapsed: v });
  },
}));
